import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, AlertTriangle } from 'lucide-react';
import { Button, Modal, Loading, EmptyState, Badge } from '@/components/ui';
import { formatDate } from '@/utils/date';
import LineEditor, { DURATION_TYPE_UNSET, hasUnsetDuration } from '@/components/affiliate/LineEditor';
import {
  affiliateApi,
  todayISODate,
  REWARD_TYPES,
  PAYOUT_MECHANISMS,
  DURATION_TYPES,
  type AffiliatePartner,
  type CreateAgreementInput,
  type DurationType,
  type LineInput,
  type PartnerAgreementWithLines,
  type PayoutMechanism,
  type RewardType,
  type RewardTemplateWithLines,
} from '@/api/affiliate';

const AGREEMENT_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success',
  scheduled: 'info',
  superseded: 'neutral',
  ended: 'neutral',
};

function durationLabel(dt: DurationType, months: number | null): string {
  return dt === 'fixed' ? `${months ?? '?'} mo` : 'while paying';
}

export default function AgreementsTab({ partner }: { partner: AffiliatePartner }) {
  const [modalOpen, setModalOpen] = useState(false);

  const { data: agreements, isLoading } = useQuery({
    queryKey: ['affiliate-agreements', partner.id],
    queryFn: () => affiliateApi.agreements.listByPartner(partner.id),
  });

  const today = todayISODate();
  const activeTodayId = useMemo(() => {
    const list = (agreements ?? []).filter((a) => a.effective_from <= today && a.status !== 'ended');
    return list.length ? list[0].id : null; // list is ordered effective_from desc
  }, [agreements, today]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-secondary">
          Effective-dated contracts. Changing terms creates a new agreement and supersedes the prior one.
        </p>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <Plus size={15} className="mr-1" /> New agreement
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading agreements…" />
      ) : !(agreements ?? []).length ? (
        <EmptyState
          title="No agreements yet"
          description="Create the partner's first agreement from a template or custom terms."
          actionLabel="New agreement"
          onAction={() => setModalOpen(true)}
        />
      ) : (
        <div className="space-y-3">
          {agreements!.map((a) => (
            <AgreementCard key={a.id} agreement={a} isActiveToday={a.id === activeTodayId} />
          ))}
        </div>
      )}

      <NewAgreementModal
        open={modalOpen}
        partner={partner}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}

function AgreementCard({
  agreement,
  isActiveToday,
}: {
  agreement: PartnerAgreementWithLines;
  isActiveToday: boolean;
}) {
  return (
    <div className="card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={AGREEMENT_STATUS_VARIANT[agreement.status] ?? 'neutral'}>{agreement.status}</Badge>
        {isActiveToday && <Badge variant="success">active today</Badge>}
        <span className="text-sm text-ink-secondary">
          Effective {formatDate(agreement.effective_from)}
        </span>
        <span className="ms-auto text-xs text-ink-muted capitalize">
          {agreement.reward_type.replace(/_/g, ' ')} · {agreement.payout_mechanism.replace(/_/g, ' ')}
        </span>
      </div>

      <div className="text-sm text-ink-secondary">
        Default (all products):{' '}
        <span className="text-ink-primary num">{agreement.commission_pct}%</span> · min{' '}
        <span className="num">{agreement.min_active_subs}</span> subs ·{' '}
        {durationLabel(agreement.duration_type, agreement.duration_months)}
      </div>

      {agreement.lines.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-navy-border">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">%</th>
                <th className="px-3 py-2">Min subs</th>
                <th className="px-3 py-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {agreement.lines.map((l) => (
                <tr key={l.id} className="border-t border-navy-border">
                  <td className="px-3 py-2 text-ink-primary">
                    {l.product_code ? `${l.system_code}/${l.product_code}` : '(all products)'}
                  </td>
                  <td className="px-3 py-2 num">{l.commission_pct}%</td>
                  <td className="px-3 py-2 num">{l.min_active_subs}</td>
                  <td className="px-3 py-2">{durationLabel(l.duration_type, l.duration_months)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {agreement.notes && <p className="text-xs text-ink-muted">{agreement.notes}</p>}
    </div>
  );
}

function NewAgreementModal({
  open,
  partner,
  onClose,
}: {
  open: boolean;
  partner: AffiliatePartner;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data: templates } = useQuery({
    queryKey: ['affiliate-templates'],
    queryFn: () => affiliateApi.templates.list(),
    enabled: open,
  });
  const { data: products } = useQuery({
    queryKey: ['affiliate-products'],
    queryFn: () => affiliateApi.catalog.productOptions(),
    enabled: open,
  });

  const [templateId, setTemplateId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayISODate());
  const [rewardType, setRewardType] = useState<RewardType>('cash_payout');
  const [payoutMechanism, setPayoutMechanism] = useState<PayoutMechanism>('bank_transfer');
  const [commissionPct, setCommissionPct] = useState(10);
  const [minActiveSubs, setMinActiveSubs] = useState(0);
  const [durationType, setDurationType] = useState<DurationType>(DURATION_TYPE_UNSET);
  const [durationMonths, setDurationMonths] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineInput[]>([]);

  const resetCustom = () => {
    setTemplateId('');
    setEffectiveFrom(todayISODate());
    setRewardType('cash_payout');
    setPayoutMechanism('bank_transfer');
    setCommissionPct(10);
    setMinActiveSubs(0);
    setDurationType(DURATION_TYPE_UNSET);
    setDurationMonths(null);
    setNotes('');
    setLines([]);
  };

  useEffect(() => {
    if (open) resetCustom();
  }, [open]);

  const applyTemplate = (tpl: RewardTemplateWithLines | undefined) => {
    if (!tpl) {
      resetCustom();
      return;
    }
    setTemplateId(tpl.id);
    setRewardType(tpl.reward_type);
    setPayoutMechanism(tpl.payout_mechanism);
    setCommissionPct(tpl.commission_pct);
    setMinActiveSubs(tpl.min_active_subs);
    setDurationType(tpl.duration_type);
    setDurationMonths(tpl.duration_months);
    setLines(
      tpl.lines.map((l) => ({
        system_code: l.system_code,
        product_code: l.product_code,
        commission_pct: l.commission_pct,
        min_active_subs: l.min_active_subs,
        duration_type: l.duration_type,
        duration_months: l.duration_months,
      })),
    );
  };

  const discountWithoutOrg = rewardType === 'discount' && !partner.linked_org_id;

  const createMut = useMutation({
    mutationFn: () => {
      const input: CreateAgreementInput = {
        template_id: templateId || null,
        reward_type: rewardType,
        commission_pct: commissionPct,
        min_active_subs: minActiveSubs,
        duration_type: durationType,
        duration_months: durationType === 'fixed' ? durationMonths : null,
        payout_mechanism: payoutMechanism,
        effective_from: effectiveFrom,
        notes: notes || null,
      };
      return affiliateApi.agreements.create(partner.id, input, lines);
    },
    onSuccess: () => {
      toast.success('Agreement created');
      qc.invalidateQueries({ queryKey: ['affiliate-agreements', partner.id] });
      qc.invalidateQueries({ queryKey: ['affiliate-partners'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  const submit = () => {
    if (discountWithoutOrg) {
      toast.error('Discount reward requires the partner to have a linked org');
      return;
    }
    if (!durationType) {
      toast.error('Duration must be set — choose fixed or unlimited');
      return;
    }
    if (durationType === 'fixed' && (!durationMonths || durationMonths < 1)) {
      toast.error('Fixed duration requires a month count');
      return;
    }
    if (hasUnsetDuration(lines)) {
      toast.error('Every product line needs a duration — choose fixed or unlimited');
      return;
    }
    createMut.mutate();
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="New agreement" size="lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">Start from template</label>
          <select
            value={templateId}
            onChange={(e) => applyTemplate((templates ?? []).find((t) => t.id === e.target.value))}
            className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
          >
            <option value="">Custom (blank)</option>
            {(templates ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} v{t.version}
                {t.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Effective from">
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
            />
          </Field>
          <Field label="Reward type">
            <select
              value={rewardType}
              onChange={(e) => setRewardType(e.target.value as RewardType)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
            >
              {REWARD_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Payout mechanism">
            <select
              value={payoutMechanism}
              onChange={(e) => setPayoutMechanism(e.target.value as PayoutMechanism)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
            >
              {PAYOUT_MECHANISMS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {discountWithoutOrg && (
          <div className="flex items-start gap-2 rounded-lg p-3 bg-warning/10 text-warning text-xs">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              This partner has no linked org, so a <b>discount</b> reward cannot be applied. Set a linked org
              or choose cash payout.
            </span>
          </div>
        )}

        <div className="rounded-lg border border-navy-border p-3 space-y-3">
          <p className="text-xs text-ink-muted">Header default (all products) — used when no product line matches.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Default %">
              <input
                type="number"
                min={0}
                max={100}
                value={commissionPct}
                onChange={(e) => setCommissionPct(Number(e.target.value))}
                className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm num"
              />
            </Field>
            <Field label="Min active subs">
              <input
                type="number"
                min={0}
                value={minActiveSubs}
                onChange={(e) => setMinActiveSubs(Number(e.target.value))}
                className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm num"
              />
            </Field>
            <Field label="Duration">
              <select
                value={durationType || ''}
                onChange={(e) => {
                  const dt = e.target.value as DurationType;
                  setDurationType(dt);
                  setDurationMonths(dt === 'fixed' ? durationMonths ?? 12 : null);
                }}
                className={`w-full h-10 px-3 rounded border bg-navy-deeper text-ink-primary text-sm ${
                  durationType ? 'border-navy-border' : 'border-danger'
                }`}
              >
                <option value="" disabled>
                  — choose —
                </option>
                {DURATION_TYPES.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
            {durationType === 'fixed' && (
              <Field label="Months">
                <input
                  type="number"
                  min={1}
                  value={durationMonths ?? 12}
                  onChange={(e) => setDurationMonths(Number(e.target.value))}
                  className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm num"
                />
              </Field>
            )}
          </div>
        </div>

        <LineEditor lines={lines} products={products ?? []} onChange={setLines} />

        <Field label="Notes (internal, optional)">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={createMut.isPending} disabled={discountWithoutOrg}>
            Create agreement
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-secondary mb-1.5">{label}</label>
      {children}
    </div>
  );
}
